import { Vector3 } from 'three';
import { TrackRuntime } from '../track/runtime';
import { Frame } from '../track/frames';
import { mergeMeshes, sweepProfile, SweepMesh } from '../geometry/sweep';
import {
  pinMesh,
  sleeveMesh,
  sleeveCavityMesh,
  solidJunctionMesh,
  trackSleeveMesh,
  trackSleeveCavityMesh,
  trackConnectorStickMesh,
  supportConnectorStickMesh,
  trackSpineJointOrigin,
  jointBasis,
  JOINT_PIN_RADIUS_M,
  beamFitPinRadius,
  beamFitKeyFlat,
  boxSleeveMesh,
  boxSleeveCavityMesh,
  type2JointExtents,
  type TrackJointSide,
} from './joints';
import {
  fixPrintSolid,
  unionSweeps,
} from './stlFixer';
import { proposeTrackCuts, closedSeamIsBetweenSupports, ObbMm, obbMaxSideMm, pointsObbMm } from './split';
import { buildWholeMeshSources, HubSpec, MemberSpec } from './wholeMesh';
import {
  HUB_MATCH_M,
  FOOTER_MATCH_M,
  cylinderMesh,
  flatFooterMesh,
  meshPoints,
  prepareSupportPrimitives,
  jointOnFooterPad,
  endOnFooterPad,
  applyFooterPrintGeom,
  applyMaxBeamLengthSplits,
  equalBeamSegmentCount,
  maxBeamLengthM,
  type BeamSplice,
} from './supportPrims';
import { hssEndplateMeshes, footerPlateSecant } from './endplate';
import {
  normalizePrintSettings,
  PrintAtom,
  PrintJob,
  PrintPart,
  PrintSettings,
  DEFAULT_PRINT_SETTINGS,
} from './types';
import { buildGroundPlane } from './groundPlane';
import {
  DEFAULT_SUPPORT_PARAMS,
  FLAT_TERRAIN,
  generateSupports,
  sampleBySupportCount,
  SupportParams,
  Terrain,
} from '../supports/generate';
import { SupportOverride, TrackSupportPrimitive } from '../supports/types';
import {
  buildGearRackMeshes,
  buildType2PlateMeshes,
  normalizeTrackShapeSettings,
  printTrackProfile,
  trackLoopsForShape,
  type2BeamJointGeom,
  Type2BeamJointGeom,
  TrackShapeSettings,
} from '../geometry/gearRack';

export {
  ensureBeamJointHubs,
  dedupeJointHubs,
  prepareSupportPrimitives,
  flatFooterMesh,
  jointOnFooterPad,
} from './supportPrims';

/**
 * Cut arc lengths that generate will use, for the live viewport overlay.
 * Interior mid-support cuts, plus s=0 when a closed circuit's station seam is
 * itself a halfway-between-supports split. Tooth-bottom snap is applied here
 * when {@link PrintSettings.splitAtToothBottom} is on — the same cuts Generate uses.
 */
export function computeTrackSplitPreviewCuts(
  runtime: TrackRuntime,
  settings: PrintSettings,
  primitives: TrackSupportPrimitive[],
  trackShape?: Partial<TrackShapeSettings> | null,
  opts?: {
    supportsEnabled?: boolean;
    supportParams?: SupportParams;
    supportOverrides?: SupportOverride[];
  },
): number[] {
  const L = runtime.totalLength;
  if (L < 1e-6) return [];
  const supportAttachS = trackCutSupportStations(runtime, primitives, opts);
  const boundaries = computeTrackSplitBoundaries(runtime, settings, primitives, trackShape, opts);
  const cuts = boundaries.filter((s) => s > 1e-6 && s < L - 1e-6);
  if (runtime.closed && closedSeamIsBetweenSupports(runtime, supportAttachS)) {
    cuts.unshift(0);
  }
  return cuts;
}

/**
 * Live overlay stations for HSSS max-beam splices (same pipeline as Generate /
 * the support mesh: footer geom, then equal-length beam cuts).
 */
export function computeBeamSplitPreviewSplices(
  primitives: TrackSupportPrimitive[],
  settings: PrintSettings,
): BeamSplice[] {
  if (primitives.length === 0 || !(settings.maxBeamLengthMm > 0)) return [];
  const { splices } = applyMaxBeamLengthSplits(applyFooterPrintGeom(primitives, settings), settings);
  return splices;
}

/** Full boundary list `[0, …cuts, L]` — the only track splits generate uses. */
export function computeTrackSplitBoundaries(
  runtime: TrackRuntime,
  settings: PrintSettings,
  primitives: TrackSupportPrimitive[],
  trackShape?: Partial<TrackShapeSettings> | null,
  opts?: {
    supportsEnabled?: boolean;
    supportParams?: SupportParams;
    supportOverrides?: SupportOverride[];
  },
): number[] {
  const shape = normalizeTrackShapeSettings(trackShape);
  const supportAttachS = trackCutSupportStations(runtime, primitives, opts);
  const toothPitchM =
    settings.splitAtToothBottom && shape.enabled ? Math.max(shape.pitch, 1e-3) : undefined;
  return proposeTrackCuts(runtime, settings.chunkSizeMm, {
    supportAttachS,
    toothPitchM,
  });
}

/**
 * Stations used to place track splits: planned supportCount samples (including
 * suppressed) and force pins. Extra hangers are ignored so they cannot add
 * more track cuts.
 */
export function trackCutSupportStations(
  runtime: TrackRuntime,
  primitives: TrackSupportPrimitive[],
  opts?: {
    supportsEnabled?: boolean;
    supportParams?: SupportParams;
    supportOverrides?: SupportOverride[];
  },
): number[] {
  const attached = supportAttachArcLengths(runtime, primitives);
  if (opts?.supportsEnabled === false) return attached;
  const includePlanned = opts?.supportParams != null || opts?.supportsEnabled === true;
  if (!includePlanned) return attached;
  const planned = sampleBySupportCount(
    runtime,
    (opts.supportParams ?? DEFAULT_SUPPORT_PARAMS).supportCount,
  );
  const forced: number[] = [];
  for (const o of opts?.supportOverrides ?? []) {
    if (o.kind === 'force' || o.kind === 'forceFoot') forced.push(o.s);
  }
  return [...planned, ...forced];
}

/** Distance from rail below which a member end is treated as track-attached. */
const TRACK_ATTACH_MAX_M = 1.25;

export interface TrackPinSpec {
  origin: Vector3;
  /** Pin axis from the support into the track (male points this way). */
  dir: Vector3;
}

interface HubPinSpec {
  origin: Vector3;
  dir: Vector3;
  hubKey: string;
  applied?: boolean;
}

interface ChainCutSpec {
  origin: Vector3;
  dir: Vector3;
  /** Pin/sleeve radius sized to fit inside the beam. */
  pinRadius: number;
  applied?: boolean;
}

/** Atomic support fragment after hub detach + HSSS max-beam cuts. */
export interface SupportPiece {
  mesh: SweepMesh;
  points: Vector3[];
  s: number;
  additives?: SweepMesh[];
  subtract?: SweepMesh[];
  trackPin?: TrackPinSpec;
  hubPos?: Vector3;
  /** Sphere radius (matches connected beam diameter). */
  hubRadius?: number;
  /** Ground-pad junction (treated like a hub for packing — never split here). */
  footerPos?: Vector3;
  hubPins?: HubPinSpec[];
  memberEnds?: { pos: Vector3; along: Vector3 }[];
  /** Horizontal footer glue-face at memberEnds[0] / [1]. */
  footerCapEnd?: [boolean, boolean];
  /** Mean beam radius for mid-beam connector sizing. */
  beamRadius?: number;
  chainId?: string;
  chainIndex?: number;
  /** One splice face (pairing id); both faces receive a female socket. */
  chainPin?: ChainCutSpec;
  /** Mating splice face; both faces receive a female socket. */
  chainSocket?: ChainCutSpec;
  /**
   * Remainder half past a track-owned elbow stump (end of beam 2).
   * Support chunk seeding prefers these before other leftovers.
   */
  fromTrackCut?: boolean;
}

/** Joint (+ optional footer) bundled with every half-beam that meets it — pack atom. */
export interface SupportPart {
  pieces: SupportPiece[];
  junction: SupportPiece | null;
}

interface TrackChunkDraft {
  s0: number;
  s1: number;
  meshes: SweepMesh[];
  subtract: SweepMesh[];
  /** True when this chunk has at least one female track socket. */
  hasConnectors: boolean;
}

/** Progress update from {@link generatePrintJob}. `fraction` is 0..1. */
export interface PrintProgress {
  fraction: number;
  message: string;
}

export type PrintProgressCallback = (p: PrintProgress) => void;

/** Notify UI and yield so the browser can paint between heavy stages. */
async function reportProgress(
  onProgress: PrintProgressCallback | undefined,
  fraction: number,
  message: string,
): Promise<void> {
  onProgress?.({ fraction: Math.min(1, Math.max(0, fraction)), message });
  await new Promise<void>((r) => setTimeout(r, 0));
}

/**
 * Print pipeline: the live overlay cuts are the only chunk boundaries.
 * 1. Track cuts: mid-support (tooth-bottom snap if that checkbox is on).
 * 2. HSS cuts: max-beam splices, same as the live support preview.
 * 3. Remesh those track intervals; mesh each already-sliced support member.
 * 4. Pack: each track interval is a chunk (rail-attach joint-part fused on).
 *    Every remaining joint-part (hub/footer + meeting beams until the next
 *    HSS splice) is its own support chunk. Cube size is a warning only.
 */
export async function generatePrintJob(
  runtime: TrackRuntime,
  settingsIn: Partial<PrintSettings>,
  opts?: {
    supportsEnabled?: boolean;
    supportParams?: SupportParams;
    supportOverrides?: SupportOverride[];
    terrain?: Terrain;
    supportPrimitives?: TrackSupportPrimitive[];
    trackShape?: Partial<TrackShapeSettings> | null;
    onProgress?: PrintProgressCallback;
  },
): Promise<PrintJob> {
  const settings = normalizePrintSettings(settingsIn);
  const trackShape = normalizeTrackShapeSettings(opts?.trackShape);
  const onProgress = opts?.onProgress;
  const warnings: string[] = [];
  const log: string[] = [];
  const logLine = (line: string) => {
    log.push(line);
  };
  const L = runtime.totalLength;
  if (L < 0.05 || runtime.centerline.track.nodes.length < 2) {
    const msg = 'Track is too short to print.';
    return { settings, cuts: [], parts: [], atoms: [], warnings: [msg], log: [msg] };
  }

  logLine('=== RCSimFable print generation log ===');
  logLine(
    `Track: closed=${runtime.closed} length=${L.toFixed(3)} m nodes=${runtime.centerline.track.nodes.length}`,
  );
  logLine(
    `Settings: chunkSizeMm=${settings.chunkSizeMm} supportCutClearanceMm=${settings.supportCutClearanceMm} ` +
      `separateSupport=${settings.separateSupport} splitAtToothBottom=${settings.splitAtToothBottom} ` +
      `hssEndplate=${settings.hssEndplateEnabled} thickness=${settings.hssEndplateThicknessMm}mm ` +
      `diam=${settings.hssEndplateDiameterMm}mm bolts=${settings.hssEndplateBoltCount} ` +
      `hole=${settings.hssEndplateBoltHoleMm}mm circle=${settings.hssEndplateBoltCircle} ` +
      `hssSplitThick=${settings.hssSplitThicknessMm}mm diam=${settings.hssSplitDiameterMm}mm ` +
      `hssSplitBolts=${settings.hssSplitBoltCount} hole=${settings.hssSplitBoltHoleMm}mm ` +
      `hssSplitCircle=${settings.hssSplitBoltCircle} ` +
      `footerThickness=${settings.footerThicknessMm}mm footerSize=${settings.footerSizeMm}mm ` +
      `footerStub=${settings.footerAttachStubMm}mm maxBeam=${settings.maxBeamLengthMm}mm ` +
      `groundPlane=${settings.groundPlaneEnabled} height=${settings.groundPlaneHeightMm}mm ` +
      `butterflyDepth=${settings.groundButterflyDepthMm}mm`,
  );

  await reportProgress(onProgress, 0.02, 'Preparing meshes…');

  const rawPrims = applyFooterPrintGeom(
    opts?.supportPrimitives ??
      (opts?.supportsEnabled === false
        ? []
        : generateSupports(
            runtime,
            opts?.terrain ?? FLAT_TERRAIN,
            opts?.supportParams,
            opts?.supportOverrides ?? [],
          ).primitives),
    settings,
  );
  const splitOpts = {
    supportsEnabled: opts?.supportsEnabled,
    supportParams: opts?.supportParams,
    supportOverrides: opts?.supportOverrides,
  };

  // Splits are finalized here — Generate only packs these atoms afterward.
  const supportAttachS = trackCutSupportStations(runtime, rawPrims, splitOpts);
  const boundaries = computeTrackSplitBoundaries(
    runtime,
    settings,
    rawPrims,
    trackShape,
    splitOpts,
  );
  const { prims: splitPrims, splices } = applyMaxBeamLengthSplits(rawPrims, settings);
  const sources = buildWholeMeshSources(runtime, splitPrims, trackShape, settings);
  logLine(
    `Supports: ${splitPrims.length} primitives, ${sources.hubs.length} hubs, ${sources.members.length} members, ${supportAttachS.length} track stations`,
  );
  logLine(
    `Track cuts: ${boundaries.length - 2} interior (${boundaries.length - 1} chunks)` +
      `${settings.splitAtToothBottom ? ' (tooth-bottom snap on)' : ''}. ` +
      `Boundaries(m): ${boundaries.map((s) => s.toFixed(2)).join(', ')}`,
  );
  logLine(`HSS max-beam splices: ${splices.length}`);

  // Step 1 — sanity-union the whole coaster (track + supports) for early warnings.
  await reportProgress(onProgress, 0.08, 'Building whole solid…');
  const whole = await unionSweeps(sources.allMeshes);
  if (whole.warning) {
    warnings.push(`Whole solid: ${whole.warning}`);
    logLine(`WARN whole solid: ${whole.warning}`);
  }
  if (whole.solid) {
    try {
      whole.solid.delete();
    } catch {
      /* ignore */
    }
  }

  // Step 2 — remesh each contiguous arc interval. Infinite plane cuts of a
  // whole-track solid would also keep parallel return runs that happen to lie
  // on the same side of the plane — those are not connected along this chunk.
  await reportProgress(onProgress, 0.28, 'Cutting track into chunks…');
  const trackChunks = await cutTrackIntoContiguousChunks(
    runtime,
    boundaries,
    trackShape,
    onProgress,
    logLine,
    {
      mergeClosedSeam:
        runtime.closed &&
        boundaries.length > 2 &&
        !closedSeamIsBetweenSupports(runtime, supportAttachS),
    },
  );

  // Step 3 — one piece per pre-split primitive. Members are already HSS-sliced;
  // hubs are existing joints, not new print cuts.
  await reportProgress(onProgress, 0.42, 'Collecting split pieces…');
  const footerPads = splitPrims
    .filter((p): p is Extract<TrackSupportPrimitive, { kind: 'footer' }> => p.kind === 'footer')
    .map((f) => ({ pos: f.pos.clone(), radius: f.radius, height: f.height }));
  const hubs = sources.hubs.filter((h) => !jointOnFooterPad(h.pos, h.radius, footerPads));
  const footers = detachFooters(splitPrims, runtime);
  const supportPieces = detachSupportsAtHubs(
    hubs,
    sources.members,
    runtime,
    settings.chunkSizeMm,
    warnings,
    footerPads,
  );
  const allSupport = [...supportPieces, ...footers];
  applyBeamSpliceChainJoints(allSupport, splices);
  linkMemberEndsToHubs(allSupport);
  linkMemberEndsToFooters(allSupport);
  logLine(
    `Support pieces: ${allSupport.length} (${supportPieces.length} members/hubs + ${footers.length} footers)`,
  );

  // Step 4 — pack preview-split atoms. Track interval + its rail-attach
  // joint-part; leftover joint-parts each become a support chunk.
  await reportProgress(onProgress, 0.48, 'Packing parts…');
  const packed = packPreviewSplitSupportOntoTrack(trackChunks, allSupport, runtime);
  const onTrackByChunk = packed.onTrackByChunk;
  let supportPool = packed.remainder;
  markTrackCutSeeds(onTrackByChunk.flat(), supportPool);

  const leftoverRailPins = supportPool.filter((p) => p.trackPin).length;
  const overflowTrackSockets = applyOverflowTrackSockets(trackChunks, supportPool, runtime);
  if (leftoverRailPins > overflowTrackSockets) {
    const msg =
      `${leftoverRailPins - overflowTrackSockets} rail-attach support piece(s) had no matching track chunk for a female socket`;
    warnings.push(msg);
    logLine(`WARN ${msg}`);
  } else if (overflowTrackSockets > 0) {
    logLine(
      `${overflowTrackSockets} leftover rail-attach beam(s) got male pin + track female socket`,
    );
  }
  logLine(
    `Packing: on-track pieces=[${onTrackByChunk.map((g) => g.length).join(',')}] ` +
      `supportPool=${supportPool.length} overflowTrackSockets=${overflowTrackSockets}`,
  );

  const supportGroups = groupSupportPieces(supportPool);
  logLine(`Support packs: ${supportGroups.length} groups (sizes ${supportGroups.map((g) => g.length).join(', ') || 'none'})`);

  const finalGroups: SupportPiece[][] = [
    ...onTrackByChunk.filter((g) => g.length > 0),
    ...supportGroups,
  ];
  // Always create female/female at every cut that crosses a pack boundary.
  applyJointsAcrossGroups(finalGroups, settings);
  applyFooterEndplates(finalGroups.flat(), settings);
  logTrackChunkSupportConnectors(onTrackByChunk, logLine);
  const jointAudit = auditSupportConnectors(finalGroups);
  for (const line of jointAudit.lines) logLine(line);
  for (const w of jointAudit.warnings) {
    warnings.push(w);
    logLine(`WARN ${w}`);
  }
  // Crossing beams in different packs stay solid otherwise — carve each out of the other.
  applyCrossPackIntersectionCuts(finalGroups);

  const parts: PrintPart[] = [];
  const nTrack = trackChunks.length;
  const nSupport = supportGroups.length;
  const needsTrackConnectorStick = trackChunks.some((c) => c.hasConnectors);
  const supportPinRadii = uniqueAppliedChainPinRadii(finalGroups.flat());
  const nConnectors = (needsTrackConnectorStick ? 1 : 0) + supportPinRadii.length;
  const nFix = Math.max(1, nTrack + nSupport + nConnectors);
  let trackIndex = 0;
  for (let ci = 0; ci < trackChunks.length; ci++) {
    const chunk = trackChunks[ci];
    const meshes: SweepMesh[] = [...chunk.meshes];
    const subtract: SweepMesh[] = [...chunk.subtract];

    for (const piece of onTrackByChunk[ci] ?? []) {
      meshes.push(piece.mesh);
      if (piece.additives) meshes.push(...piece.additives);
      if (piece.subtract) subtract.push(...piece.subtract);
    }

    trackIndex += 1;
    const frac = 0.52 + (0.35 * (trackIndex - 1)) / nFix;
    await reportProgress(
      onProgress,
      frac,
      `Solidifying track ${trackIndex}/${nTrack}…`,
    );
    const fixedResult = await fixPrintSolid(meshes, { subtract });
    if (fixedResult.warning) {
      warnings.push(`Track ${pad(trackIndex)}: ${fixedResult.warning}`);
      logLine(`WARN Track ${pad(trackIndex)}: ${fixedResult.warning}`);
    }
    const mesh = fixedResult.mesh;
    const obb = pointsObbMm(meshPoints(mesh));
    const oversized = obbMaxSideMm(obb) > settings.chunkSizeMm + 1e-3;
    if (oversized) {
      const msg = `Track part ${pad(trackIndex)} OBB ${fmtObb(obb)} exceeds ${settings.chunkSizeMm} mm cube`;
      warnings.push(msg);
      logLine(`WARN ${msg}`);
    }
    logLine(
      `Part track-${pad(trackIndex)}: s=[${chunk.s0.toFixed(2)},${chunk.s1.toFixed(2)}] ` +
        `obb=${fmtObb(obb)} fixed=${fixedResult.fixed} oversized=${oversized} ` +
        `supportPieces=${(onTrackByChunk[ci] ?? []).length}`,
    );
    parts.push({
      id: `track-${pad(trackIndex)}`,
      kind: 'track',
      s0: chunk.s0,
      s1: chunk.s1,
      mesh,
      obbMm: obb,
      oversized,
      fixed: fixedResult.fixed,
      fixWarning: fixedResult.warning,
    });
  }

  let supportIndex = 0;
  for (const group of supportGroups) {
    supportIndex += 1;
    const meshes: SweepMesh[] = [];
    const subtract: SweepMesh[] = [];
    let s0 = Infinity;
    let s1 = -Infinity;
    for (const p of group) {
      meshes.push(p.mesh);
      if (p.additives) meshes.push(...p.additives);
      if (p.subtract) subtract.push(...p.subtract);
      s0 = Math.min(s0, p.s);
      s1 = Math.max(s1, p.s);
    }
    const frac = 0.52 + (0.35 * (nTrack + supportIndex - 1)) / nFix;
    await reportProgress(
      onProgress,
      frac,
      `Solidifying support ${supportIndex}/${nSupport}…`,
    );
    const fixedResult = await fixPrintSolid(meshes, { subtract });
    if (fixedResult.warning) {
      warnings.push(`Support ${pad(supportIndex)}: ${fixedResult.warning}`);
      logLine(`WARN Support ${pad(supportIndex)}: ${fixedResult.warning}`);
    }
    const mesh = fixedResult.mesh;
    const obb = pointsObbMm(meshPoints(mesh));
    const oversized = obbMaxSideMm(obb) > settings.chunkSizeMm + 1e-3;
    if (oversized) {
      const msg = `Support part ${pad(supportIndex)} OBB ${fmtObb(obb)} exceeds ${settings.chunkSizeMm} mm cube`;
      warnings.push(msg);
      logLine(`WARN ${msg}`);
    }
    logLine(
      `Part support-${pad(supportIndex)}: s≈[${Number.isFinite(s0) ? s0.toFixed(2) : '?'},${Number.isFinite(s1) ? s1.toFixed(2) : '?'}] ` +
        `obb=${fmtObb(obb)} fixed=${fixedResult.fixed} oversized=${oversized} pieces=${group.length}`,
    );
    parts.push({
      id: `support-${pad(supportIndex)}`,
      kind: 'support',
      s0: Number.isFinite(s0) ? s0 : 0,
      s1: Number.isFinite(s1) ? s1 : 0,
      mesh,
      obbMm: obb,
      oversized,
      fixed: fixedResult.fixed,
      fixWarning: fixedResult.warning,
    });
  }

  if (needsTrackConnectorStick) {
    await reportProgress(
      onProgress,
      0.52 + (0.35 * (nTrack + nSupport)) / nFix,
      'Solidifying connector pin…',
    );
    parts.push(
      await solidifyConnectorPart(
        'connector-pin',
        buildTrackConnectorStickMesh(runtime, trackShape),
        'reusable male stick for all track joints',
        settings,
        warnings,
        logLine,
      ),
    );
  }

  for (let i = 0; i < supportPinRadii.length; i++) {
    const id = i === 0 ? 'support-connector-pin' : `support-connector-pin-${i + 1}`;
    const pinR = supportPinRadii[i]!;
    await reportProgress(
      onProgress,
      0.52 + (0.35 * (nTrack + nSupport + (needsTrackConnectorStick ? 1 : 0) + i)) / nFix,
      'Solidifying support connector pin…',
    );
    parts.push(
      await solidifyConnectorPart(
        id,
        buildSupportConnectorStickMesh(runtime, pinR, i),
        `reusable male stick for support-beam splices (r=${pinR.toFixed(3)})`,
        settings,
        warnings,
        logLine,
      ),
    );
  }

  await appendGroundPlaneParts(
    parts,
    runtime,
    splitPrims,
    settings,
    warnings,
    logLine,
    onProgress,
  );

  const atoms = collectPrintAtoms(trackChunks, onTrackByChunk, supportGroups, parts);
  const nConnOut = parts.filter((p) => p.kind === 'connector').length;
  const nGroundOut = parts.filter((p) => p.kind === 'ground').length;
  logLine(
    `Done: ${parts.length} parts (${trackIndex} track, ${supportIndex} support` +
      `${nGroundOut > 0 ? `, ${nGroundOut} ground` : ''}` +
      `${nConnOut > 0 ? `, ${nConnOut} connector` : ''}), ` +
      `${atoms.length} split parts, ${warnings.length} warning(s)`,
  );
  await reportProgress(onProgress, 1, 'Done');
  const cuts = trackChunks
    .flatMap((c) => [c.s0, c.s1])
    .filter((s) => s > 1e-6 && s < L - 1e-6)
    .sort((a, b) => a - b)
    .filter((s, i, arr) => i === 0 || s - arr[i - 1]! > 1e-6);
  return { settings, cuts, parts, atoms, warnings, log };
}

/**
 * Build one mesh per contiguous arc interval [s0, s1].
 * Remeshes only that interval — never plane-cuts a whole-track solid, which
 * would also keep nearby parallel return runs that share a half-space.
 * Closed circuits whose wrap midpoint is not the s=0 seam merge the first
 * and last remeshes so the only joints sit halfway between supports.
 */
async function cutTrackIntoContiguousChunks(
  runtime: TrackRuntime,
  boundaries: number[],
  trackShape: TrackShapeSettings,
  onProgress?: PrintProgressCallback,
  logLine?: (line: string) => void,
  opts?: { mergeClosedSeam?: boolean },
): Promise<TrackChunkDraft[]> {
  const L = runtime.totalLength;
  const drafts: TrackChunkDraft[] = [];
  const intervals = boundaries.length - 1;
  const nChunks = Math.max(1, intervals);
  const cutFrac = (index: number) => 0.28 + (0.12 * index) / nChunks;
  const mergeClosedSeam = opts?.mergeClosedSeam === true && intervals > 1;
  const closedWrap = runtime.closed && intervals > 1 && !mergeClosedSeam;

  for (let i = 0; i < intervals; i++) {
    const s0 = boundaries[i]!;
    const s1 = boundaries[i + 1]!;
    if (s1 - s0 < 1e-6) continue;
    await reportProgress(
      onProgress,
      cutFrac(i),
      `Cutting track into chunks… ${i + 1}/${nChunks}`,
    );
    drafts.push(
      trackChunkFromRemesh(runtime, s0, s1, L, trackShape, {
        closedWrapStart: closedWrap && s0 <= 1e-6,
        closedWrapEnd: closedWrap && s1 >= L - 1e-6,
        closeFullLoop: runtime.closed && intervals === 1 && s0 <= 1e-6 && s1 >= L - 1e-6,
      }),
    );
  }

  if (mergeClosedSeam && drafts.length > 1) {
    const first = drafts[0]!;
    const last = drafts[drafts.length - 1]!;
    drafts[0] = {
      s0: last.s0,
      s1: first.s1,
      meshes: [...last.meshes, ...first.meshes],
      subtract: [...last.subtract, ...first.subtract],
      hasConnectors: last.hasConnectors || first.hasConnectors,
    };
    drafts.pop();
    logLine?.('Closed-track wrap: first+last remeshes merged (no extra joint at s=0)');
  } else if (closedWrap) {
    logLine?.(
      'Closed-track wrap seam: female sleeve on last chunk @ s=L, female sleeve on first chunk @ s=0',
    );
  }

  for (let i = 0; i < drafts.length; i++) {
    const d = drafts[i]!;
    logLine?.(
      `Track chunk ${i + 1}: s=[${d.s0.toFixed(2)},${d.s1.toFixed(2)}] connectors=[${trackChunkConnectorLabel(runtime, d, L, closedWrap)}]`,
    );
  }
  return drafts;
}

function trackChunkConnectorLabel(
  runtime: TrackRuntime,
  draft: TrackChunkDraft,
  L: number,
  closedWrap: boolean,
): string {
  if (runtime.closed && draft.s0 <= 1e-6 && draft.s1 >= L - 1e-6 && draft.s0 <= draft.s1) {
    return 'closedLoop';
  }
  const wrapping = draft.s0 > draft.s1 + 1e-6;
  const femaleStart = wrapping || draft.s0 > 1e-6 || (closedWrap && draft.s0 <= 1e-6);
  const femaleEnd = wrapping || draft.s1 < L - 1e-6 || (closedWrap && draft.s1 >= L - 1e-6);
  const connectors: string[] = [];
  if (femaleStart) connectors.push('female@start');
  if (femaleEnd) connectors.push('female@end');
  return connectors.join('+') || 'none';
}

function trackChunkFromRemesh(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  L: number,
  trackShape: TrackShapeSettings,
  opts?: {
    /** Female socket at s≈0 for the closed-circuit wrap. */
    closedWrapStart?: boolean;
    /** Female socket at s≈L for the closed-circuit wrap. */
    closedWrapEnd?: boolean;
    /** Whole closed circuit as one chunk — continuous loop, no seam joint. */
    closeFullLoop?: boolean;
  },
): TrackChunkDraft {
  const closeFullLoop = opts?.closeFullLoop === true;
  const meshes: SweepMesh[] = remeshTrackInterval(runtime, s0, s1, trackShape, {
    closeLoop: closeFullLoop,
  });
  const subtract: SweepMesh[] = [];
  if (closeFullLoop) {
    return { s0, s1, meshes, subtract, hasConnectors: false };
  }
  // Track Type 2 keys the click-fit into its rectangular beam bar; Type 1 (and a
  // Type 2 with no beam) falls back to the round spine connector. Both faces
  // are female; the male stick is a separate reusable part.
  const type2Geom = type2BeamJointGeom(trackShape);
  let hasConnectors = false;
  // Female at chunk start for interior cuts, or the closed wrap at s=0.
  if (s0 > 1e-6 || opts?.closedWrapStart) {
    addTrackFemale(meshes, subtract, runtime.frameAt(s0), type2Geom, 'start');
    hasConnectors = true;
  }
  // Female at chunk end for interior cuts, or the closed wrap at s=L.
  if (s1 < L - 1e-6 || opts?.closedWrapEnd) {
    addTrackFemale(meshes, subtract, runtime.frameAt(s1), type2Geom, 'end');
    hasConnectors = true;
  }
  return { s0, s1, meshes, subtract, hasConnectors };
}

/** Centre of the Track Type 2 beam bar at a frame (below the rails, along +up). */
function type2BeamJointOrigin(f: Frame, geom: Type2BeamJointGeom): Vector3 {
  return f.railPos.clone().addScaledVector(f.up, geom.centerY);
}

function addTrackFemale(
  meshes: SweepMesh[],
  subtract: SweepMesh[],
  f: Frame,
  type2Geom: Type2BeamJointGeom | null,
  side: TrackJointSide,
): void {
  if (type2Geom) {
    addType2TrackFemale(meshes, subtract, f, type2Geom, side);
    return;
  }
  meshes.push(trackSleeveMesh(f, side));
  subtract.push(trackSleeveCavityMesh(f, side));
}

/** Spare male stick, posed beside the layout start so the preview does not overlap the track. */
function buildTrackConnectorStickMesh(
  runtime: TrackRuntime,
  trackShape: TrackShapeSettings,
): SweepMesh {
  const f = runtime.frameAt(0);
  const type2Geom = type2BeamJointGeom(trackShape);
  const offset = f.right.clone().multiplyScalar(4);
  if (type2Geom) {
    const origin = type2BeamJointOrigin(f, type2Geom).add(offset);
    return trackConnectorStickMesh(
      origin,
      f.tan,
      f.right,
      f.up,
      type2JointExtents(type2Geom.halfW, type2Geom.halfH),
    );
  }
  return trackConnectorStickMesh(
    trackSpineJointOrigin(f).add(offset),
    f.tan,
    f.right,
    f.up,
  );
}

/** Spare support stick, posed on the opposite side of the layout start from the track stick. */
function buildSupportConnectorStickMesh(
  runtime: TrackRuntime,
  pinRadius: number,
  slot: number,
): SweepMesh {
  const f = runtime.frameAt(0);
  const origin = trackSpineJointOrigin(f).add(
    f.right.clone().multiplyScalar(-(4 + slot * 2)),
  );
  return supportConnectorStickMesh(origin, f.tan, pinRadius);
}

function uniqueAppliedChainPinRadii(pieces: SupportPiece[]): number[] {
  const radii: number[] = [];
  const seen = new Set<string>();
  for (const p of pieces) {
    if (!p.chainPin?.applied) continue;
    const key = p.chainPin.pinRadius.toFixed(4);
    if (seen.has(key)) continue;
    seen.add(key);
    radii.push(p.chainPin.pinRadius);
  }
  return radii;
}

async function solidifyConnectorPart(
  id: string,
  meshIn: SweepMesh,
  detail: string,
  settings: PrintSettings,
  warnings: string[],
  logLine: (line: string) => void,
): Promise<PrintPart> {
  const fixedResult = await fixPrintSolid([meshIn]);
  if (fixedResult.warning) {
    warnings.push(`${id}: ${fixedResult.warning}`);
    logLine(`WARN ${id}: ${fixedResult.warning}`);
  }
  const mesh = fixedResult.mesh;
  const obb = pointsObbMm(meshPoints(mesh));
  const oversized = obbMaxSideMm(obb) > settings.chunkSizeMm + 1e-3;
  if (oversized) {
    const msg = `${id} OBB ${fmtObb(obb)} exceeds ${settings.chunkSizeMm} mm cube`;
    warnings.push(msg);
    logLine(`WARN ${msg}`);
  }
  logLine(
    `Part ${id}: ${detail} obb=${fmtObb(obb)} fixed=${fixedResult.fixed} oversized=${oversized}`,
  );
  return {
    id,
    kind: 'connector',
    s0: 0,
    s1: 0,
    mesh,
    obbMm: obb,
    oversized,
    fixed: fixedResult.fixed,
    fixWarning: fixedResult.warning,
  };
}

async function appendGroundPlaneParts(
  parts: PrintPart[],
  runtime: TrackRuntime,
  primitives: TrackSupportPrimitive[],
  settings: PrintSettings,
  warnings: string[],
  logLine: (line: string) => void,
  onProgress?: PrintProgressCallback,
): Promise<void> {
  const built = buildGroundPlane(runtime, primitives, settings);
  for (const line of built.log) logLine(line);
  for (const w of built.warnings) {
    warnings.push(w);
    logLine(`WARN ${w}`);
  }
  const nTiles = built.tiles.length;
  const nConn = built.connectorKey ? 1 : 0;
  const nFix = Math.max(1, nTiles + nConn);
  for (let i = 0; i < built.tiles.length; i++) {
    const tile = built.tiles[i]!;
    await reportProgress(
      onProgress,
      0.88 + (0.1 * i) / nFix,
      `Solidifying ground ${i + 1}/${nTiles}…`,
    );
    const fixedResult = await fixPrintSolid(tile.meshes, { subtract: tile.subtract });
    if (fixedResult.warning) {
      warnings.push(`${tile.id}: ${fixedResult.warning}`);
      logLine(`WARN ${tile.id}: ${fixedResult.warning}`);
    }
    const mesh = fixedResult.mesh;
    const obb = pointsObbMm(meshPoints(mesh));
    const oversized = obbMaxSideMm(obb) > settings.chunkSizeMm + 1e-3;
    if (oversized) {
      const msg = `${tile.id} OBB ${fmtObb(obb)} exceeds ${settings.chunkSizeMm} mm cube`;
      warnings.push(msg);
      logLine(`WARN ${msg}`);
    }
    logLine(
      `Part ${tile.id}: slab+${tile.clips.length} clip(s)+${tile.keys.length} butterfly key(s) ` +
        `obb=${fmtObb(obb)} fixed=${fixedResult.fixed} oversized=${oversized}`,
    );
    parts.push({
      id: tile.id,
      kind: 'ground',
      s0: 0,
      s1: 0,
      mesh,
      obbMm: obb,
      oversized,
      fixed: fixedResult.fixed,
      fixWarning: fixedResult.warning,
    });
  }
  if (built.connectorKey) {
    await reportProgress(onProgress, 0.97, 'Solidifying ground butterfly key…');
    parts.push(
      await solidifyConnectorPart(
        'ground-butterfly-key',
        built.connectorKey,
        'reusable butterfly / bow-tie key for ground-plane tile joints',
        settings,
        warnings,
        logLine,
      ),
    );
  }
}

/**
 * Female rectangular socket in the Type 2 beam bar: outer flush with the beam,
 * inner sized to the peg. Start uses −tan and end uses +tan so the collar sits
 * inside this chunk. Cavity is subtracted after union to keep the click-fit open.
 */
function addType2TrackFemale(
  meshes: SweepMesh[],
  subtract: SweepMesh[],
  f: Frame,
  geom: Type2BeamJointGeom,
  side: TrackJointSide,
): void {
  const origin = type2BeamJointOrigin(f, geom);
  const ext = type2JointExtents(geom.halfW, geom.halfH);
  const basis = jointBasis(origin, f.tan, f.right, f.up);
  let femaleDir: Vector3;
  switch (side) {
    case 'start':
      femaleDir = basis.dir.clone().multiplyScalar(-1);
      break;
    case 'end':
      femaleDir = basis.dir.clone();
      break;
    default: {
      const _never: never = side;
      return _never;
    }
  }
  meshes.push(
    boxSleeveMesh(
      origin,
      femaleDir,
      basis.right,
      basis.up,
      ext.cavityHalfW,
      ext.cavityHalfH,
      ext.outerHalfW,
      ext.outerHalfH,
    ),
  );
  subtract.push(
    boxSleeveCavityMesh(origin, femaleDir, basis.right, basis.up, ext.cavityHalfW, ext.cavityHalfH),
  );
}

const PRINT_SAMPLE_STEP = 0.28;

function remeshTrackInterval(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  trackShape: TrackShapeSettings,
  opts?: { closeLoop?: boolean },
): SweepMesh[] {
  const len = Math.max(s1 - s0, 0.01);
  const n = Math.max(2, Math.ceil(len / PRINT_SAMPLE_STEP));
  const frames: Frame[] = [];
  for (let i = 0; i <= n; i++) frames.push(runtime.frameAt(s0 + (len * i) / n));
  const profile = printTrackProfile(trackShape);
  const { rails, spine } = trackLoopsForShape(trackShape, 'print', 14);
  const closeLoop = opts?.closeLoop === true;
  const pieces: SweepMesh[] = [
    ...rails.map((loop) => sweepProfile(frames, loop, { capEnds: !closeLoop, closeLoop })),
    ...spine.map((loop) => sweepProfile(frames, loop, { capEnds: !closeLoop, closeLoop })),
  ];
  pieces.push(...buildType2PlateMeshes(runtime, s0, s1, trackShape, 'print'));
  pieces.push(...buildGearRackMeshes(runtime, s0, s1, trackShape, profile));
  return pieces;
}

/**
 * One piece per hub sphere and each already-HSS-sliced member. Hubs are not
 * print cuts — packing keeps each joint with the beams that meet it.
 */
function detachSupportsAtHubs(
  hubs: HubSpec[],
  members: MemberSpec[],
  runtime: TrackRuntime,
  chunkSizeMm: number,
  warnings: string[],
  footerPads: { pos: Vector3; radius: number; height: number }[] = [],
): SupportPiece[] {
  const pieces: SupportPiece[] = [];

  for (const hub of hubs) {
    const mesh = solidJunctionMesh(hub.pos, hub.radius);
    if (obbMaxSideMm(pointsObbMm(meshPoints(mesh))) > chunkSizeMm + 1e-3) {
      warnings.push(
        `Support joint at (${hub.pos.x.toFixed(1)},${hub.pos.y.toFixed(1)},${hub.pos.z.toFixed(1)}) exceeds build cube`,
      );
    }
    pieces.push({
      mesh,
      points: meshPoints(mesh),
      s: nearestS(runtime, hub.pos),
      hubPos: hub.pos.clone(),
      hubRadius: hub.radius,
    });
  }

  for (const mem of members) {
    pieces.push(memberPieceFromSpec(mem, runtime, footerPads));
  }

  return pieces;
}

function memberPieceFromSpec(
  mem: MemberSpec,
  runtime: TrackRuntime,
  footerPads: { pos: Vector3; radius: number; height: number }[],
): SupportPiece {
  const capA = endOnFooterPad(mem.a, footerPads);
  const capB = endOnFooterPad(mem.b, footerPads);
  const mesh = cylinderMesh(mem.a, mem.b, mem.radiusA, mem.radiusB, {
    horizontalCapA: capA,
    horizontalCapB: capB,
  });
  const along = new Vector3().subVectors(mem.b, mem.a);
  const piece: Omit<SupportPiece, 's'> = {
    mesh,
    points: meshPoints(mesh),
    beamRadius: (mem.radiusA + mem.radiusB) * 0.5,
    footerCapEnd: [capA, capB],
  };
  if (along.lengthSq() >= 1e-12) {
    along.normalize();
    piece.memberEnds = [
      { pos: mem.a.clone(), along: along.clone().multiplyScalar(-1) },
      { pos: mem.b.clone(), along: along.clone() },
    ];
  }
  if (mem.kind === 'column' || mem.kind === 'stub') {
    // Footer attach stubs are also kind `stub` — they sit on the pad, not the rail.
    if (!(mem.kind === 'stub' && (capA || capB))) {
      markTrackAttachIfNearRail([piece], mem.a, mem.b, runtime);
    }
  }
  return { ...piece, s: nearestS(runtime, mem.a.clone().lerp(mem.b, 0.5)) };
}

/**
 * Wire HSS max-beam splices (computed before Generate) as mid-beam pin/socket
 * pairs. Does not invent new cuts — only labels the existing splice stations.
 */
function applyBeamSpliceChainJoints(pieces: SupportPiece[], splices: BeamSplice[]): void {
  if (splices.length === 0) return;
  const beams = pieces.filter(
    (p) => !isJunctionPiece(p) && !!p.memberEnds && p.memberEnds.length >= 2,
  );
  for (let i = 0; i < splices.length; i++) {
    const sp = splices[i]!;
    const hits: { piece: SupportPiece; endIdx: number }[] = [];
    for (const b of beams) {
      const ends = b.memberEnds!;
      for (let e = 0; e < ends.length; e++) {
        if (ends[e]!.pos.distanceTo(sp.pos) <= CHAIN_CUT_MATCH_M) {
          hits.push({ piece: b, endIdx: e });
        }
      }
    }
    if (hits.length < 2) continue;
    const dir = sp.dir.clone();
    if (dir.lengthSq() < 1e-12) continue;
    dir.normalize();
    const score = (h: { piece: SupportPiece; endIdx: number }): number => {
      const far = h.piece.memberEnds![1 - h.endIdx]!.pos;
      return far.clone().sub(sp.pos).dot(dir);
    };
    hits.sort((a, b) => score(a) - score(b));
    const male = hits[0]!.piece;
    const female = hits[hits.length - 1]!.piece;
    if (male === female) continue;
    const chainId = `hss:${i}:${sp.pos.x.toFixed(3)},${sp.pos.y.toFixed(3)},${sp.pos.z.toFixed(3)}`;
    const pinR = beamFitPinRadius((male.beamRadius ?? female.beamRadius ?? sp.radius + 1e-4));
    male.chainId = male.chainId ?? chainId;
    female.chainId = female.chainId ?? chainId;
    if (!male.chainPin) {
      male.chainPin = { origin: sp.pos.clone(), dir: dir.clone(), pinRadius: pinR };
    }
    if (!female.chainSocket) {
      female.chainSocket = { origin: sp.pos.clone(), dir: dir.clone(), pinRadius: pinR };
    }
  }
}

function detachFooters(
  rawPrims: TrackSupportPrimitive[],
  runtime: TrackRuntime,
): SupportPiece[] {
  const prepared = prepareSupportPrimitives(rawPrims);
  const out: SupportPiece[] = [];
  for (const prim of prepared) {
    if (prim.kind !== 'footer') continue;
    const h = Math.max(prim.height, 0.12);
    // Junction is the pad top where the column sits (same as sphere joints).
    const top = new Vector3(prim.pos.x, prim.pos.y + h, prim.pos.z);
    const mesh = flatFooterMesh(prim.pos, prim.radius, prim.height);
    out.push({
      mesh,
      points: meshPoints(mesh),
      s: nearestS(runtime, prim.pos),
      footerPos: top,
    });
  }
  return out;
}

function hubKey(pos: Vector3): string {
  return `${pos.x.toFixed(3)},${pos.y.toFixed(3)},${pos.z.toFixed(3)}`;
}

/** Wire member endpoints (and column bodies near hubs) to joint spheres as male pin specs. */
export function linkMemberEndsToHubs(pieces: SupportPiece[]): void {
  const hubs = pieces.filter((p) => p.hubPos);
  for (const piece of pieces) {
    if (!piece.memberEnds || piece.memberEnds.length === 0) continue;
    const pins: HubPinSpec[] = [...(piece.hubPins ?? [])];
    for (const end of piece.memberEnds) {
      let best: SupportPiece | null = null;
      let bestDist = HUB_MATCH_M;
      for (const hub of hubs) {
        const d = hub.hubPos!.distanceTo(end.pos);
        if (d < bestDist) {
          bestDist = d;
          best = hub;
        }
      }
      if (!best?.hubPos) continue;
      const dir = end.along.clone().normalize();
      pins.push({
        origin: end.pos.clone(),
        dir,
        hubKey: hubKey(best.hubPos),
      });
    }
    if (pins.length > 0) piece.hubPins = pins;
  }

  for (const hub of hubs) {
    if (!hub.hubPos) continue;
    const key = hubKey(hub.hubPos);
    for (const piece of pieces) {
      if (!piece.memberEnds || piece.memberEnds.length < 2) continue;
      if ((piece.hubPins ?? []).some((p) => p.hubKey === key)) continue;
      const a = piece.memberEnds[0].pos;
      const b = piece.memberEnds[piece.memberEnds.length - 1].pos;
      const hit = closestPointOnSegment(hub.hubPos, a, b);
      if (hit.distanceTo(hub.hubPos) > HUB_MATCH_M) continue;
      const along = new Vector3().subVectors(b, a);
      if (along.lengthSq() < 1e-12) continue;
      along.normalize();
      const intoHub = new Vector3().subVectors(hub.hubPos, hit);
      if (intoHub.lengthSq() < 1e-12) {
        intoHub.copy(along);
      } else {
        intoHub.normalize();
      }
      piece.hubPins = [
        ...(piece.hubPins ?? []),
        { origin: hit.clone(), dir: intoHub, hubKey: key },
      ];
    }
  }
}

/** Wire member endpoints near ground pads — same role as hub spheres for chunking. */
export function linkMemberEndsToFooters(pieces: SupportPiece[]): void {
  const footers = pieces.filter((p) => p.footerPos);
  for (const piece of pieces) {
    if (!piece.memberEnds || piece.memberEnds.length === 0) continue;
    const pins: HubPinSpec[] = [...(piece.hubPins ?? [])];
    for (const end of piece.memberEnds) {
      let best: SupportPiece | null = null;
      let bestDist = FOOTER_MATCH_M;
      for (const foot of footers) {
        const d = horizontalDist(foot.footerPos!, end.pos);
        const dy = Math.abs(foot.footerPos!.y - end.pos.y);
        if (d < bestDist && dy < 0.18) {
          bestDist = d;
          best = foot;
        }
      }
      if (!best?.footerPos) continue;
      const key = hubKey(best.footerPos);
      if (pins.some((p) => p.hubKey === key)) continue;
      const dir = end.along.clone().normalize();
      pins.push({
        origin: end.pos.clone(),
        dir,
        hubKey: key,
      });
    }
    if (pins.length > 0) piece.hubPins = pins;
  }
}

function horizontalDist(a: Vector3, b: Vector3): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

function closestPointOnSegment(p: Vector3, a: Vector3, b: Vector3): Vector3 {
  const ab = new Vector3().subVectors(b, a);
  const lenSq = ab.lengthSq();
  if (lenSq < 1e-18) return a.clone();
  const t = Math.min(1, Math.max(0, new Vector3().subVectors(p, a).dot(ab) / lenSq));
  return a.clone().addScaledVector(ab, t);
}

export function applyCrossPackHubJoints(groupA: SupportPiece[], groupB: SupportPiece[]): void {
  connectHubs(groupA, groupB);
  connectHubs(groupB, groupA);
}

function connectHubs(hubGroup: SupportPiece[], memberGroup: SupportPiece[]): void {
  for (const member of memberGroup) {
    for (const pin of member.hubPins ?? []) {
      if (pin.applied) continue;
      const matchingHubs = hubGroup.filter((h) => {
        const pos = h.hubPos ?? h.footerPos;
        return pos != null && hubKey(pos) === pin.hubKey;
      });
      if (matchingHubs.length === 0) continue;
      // Footer pads are flat glue faces — never punch a pin/sleeve through them.
      if (matchingHubs.some((h) => h.footerPos)) continue;

      const beamR =
        member.beamRadius ??
        matchingHubs[0].hubRadius ??
        JOINT_PIN_RADIUS_M;
      const pinR = beamFitPinRadius(beamR);
      const keyFlat = beamFitKeyFlat(pinR);
      const pinSolid = pinMesh(pin.origin, pin.dir, undefined, undefined, pinR, keyFlat);
      member.additives = [...(member.additives ?? []), pinSolid];
      member.points = [...member.points, ...meshPoints(pinSolid)];

      // Pin dir points into the hub; sleeve convention extends along −dir, so flip
      // (and keep shared right/up) so the socket lives inside the hub sphere.
      const basis = jointBasis(pin.origin, pin.dir);
      const femaleDir = basis.dir.clone().multiplyScalar(-1);
      for (const hub of matchingHubs) {
        hub.additives = [
          ...(hub.additives ?? []),
          sleeveMesh(pin.origin, femaleDir, basis.right, basis.up, pinR, keyFlat),
        ];
        hub.subtract = [
          ...(hub.subtract ?? []),
          sleeveCavityMesh(pin.origin, femaleDir, basis.right, basis.up, pinR, keyFlat),
        ];
      }
      pin.applied = true;
    }
  }
}

export function applyCrossGroupChainJoints(
  groupA: SupportPiece[],
  groupB: SupportPiece[],
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): void {
  connectChainCuts(groupA, groupB, settings);
  connectChainCuts(groupB, groupA, settings);
}

/** Max distance between a mid-beam pin origin and its mating socket origin. */
const CHAIN_CUT_MATCH_M = 0.08;

type ChainPair = {
  male: SupportPiece;
  female: SupportPiece;
  dist: number;
  sameChain: boolean;
};

/**
 * Candidate female for a mid-beam pin. Socket origin must lie on the cut —
 * index alone is not enough (packing renumbers only one side of the split).
 */
function chainCutDistance(male: SupportPiece, female: SupportPiece): number | null {
  if (!male.chainPin || !female.chainSocket) return null;
  if (female.chainSocket.applied) return null;
  const dist = female.chainSocket.origin.distanceTo(male.chainPin.origin);
  if (dist > CHAIN_CUT_MATCH_M) return null;
  return dist;
}

function findChainSocketMate(
  male: SupportPiece,
  candidates: Iterable<SupportPiece>,
): SupportPiece | undefined {
  if (!male.chainPin) return undefined;

  let best: SupportPiece | undefined;
  let bestDist = Infinity;
  let bestSameChain = false;
  for (const p of candidates) {
    if (p === male) continue;
    const dist = chainCutDistance(male, p);
    if (dist == null) continue;
    const sameChain = male.chainId != null && p.chainId === male.chainId;
    // Prefer same-chain mates; among equals, closer origin wins.
    if (
      !best ||
      (sameChain && !bestSameChain) ||
      (sameChain === bestSameChain && dist < bestDist)
    ) {
      best = p;
      bestDist = dist;
      bestSameChain = sameChain;
    }
  }
  return best;
}

/**
 * Female socket on a support splice face. `towardMate` is the outward axis
 * (mouth faces the other piece); {@link sleeveMesh} extends along −dir into this part.
 * `keyRight` / `keyUp` must be shared across both faces so the D-flat stays on
 * the same world side (flipping `towardMate` alone would rotate the key 180°).
 */
function addSupportFemaleSocket(
  piece: SupportPiece,
  origin: Vector3,
  towardMate: Vector3,
  pinRadius: number,
  keyRight: Vector3,
  keyUp: Vector3,
): void {
  const keyFlat = beamFitKeyFlat(pinRadius);
  const sleeve = sleeveMesh(origin, towardMate, keyRight, keyUp, pinRadius, keyFlat);
  piece.additives = [...(piece.additives ?? []), sleeve];
  piece.points = [...piece.points, ...meshPoints(sleeve)];
  piece.subtract = [
    ...(piece.subtract ?? []),
    sleeveCavityMesh(origin, towardMate, keyRight, keyUp, pinRadius, keyFlat),
  ];
}

function applyChainJoint(
  male: SupportPiece,
  female: SupportPiece,
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): void {
  if (!male.chainPin || !female.chainSocket) return;
  if (male.chainPin.applied || female.chainSocket.applied) return;
  const pin = male.chainPin;
  const sock = female.chainSocket;
  const tubeR = male.beamRadius ?? female.beamRadius ?? pin.pinRadius;

  if (settings.hssEndplateEnabled) {
    // Mid-beam pack splits also get HSSS bolted flanges (same as live max-beam preview).
    addHssEndplate(male, pin.origin, pin.dir.clone().multiplyScalar(-1), tubeR, settings, {
      boltHeads: true,
      beamSplit: true,
    });
    addHssEndplate(female, sock.origin, sock.dir, tubeR, settings, {
      boltHeads: true,
      beamSplit: true,
    });
  }

  // Both splice faces are female; the male stick is a separate reusable part.
  // Shared key frame (from the splice axis) keeps the D-flat continuous.
  const pinR = pin.pinRadius;
  const keyBasis = jointBasis(pin.origin, pin.dir);
  addSupportFemaleSocket(male, pin.origin, pin.dir, pinR, keyBasis.right, keyBasis.up);
  addSupportFemaleSocket(
    female,
    sock.origin,
    sock.dir.clone().multiplyScalar(-1),
    pinR,
    keyBasis.right,
    keyBasis.up,
  );

  pin.applied = true;
  sock.applied = true;
}

function addHssEndplate(
  piece: SupportPiece,
  origin: Vector3,
  intoPart: Vector3,
  tubeRadius: number,
  settings: PrintSettings,
  opts?: { tubeSecant?: number; maxThickness?: number; boltHeads?: boolean; beamSplit?: boolean },
): void {
  if (intoPart.lengthSq() < 1e-12) return;
  const plates = hssEndplateMeshes(origin, intoPart, tubeRadius, settings, opts);
  if (plates.solids.length === 0 && plates.holes.length === 0) return;
  if (plates.solids.length > 0) {
    piece.additives = [...(piece.additives ?? []), ...plates.solids];
    for (const solid of plates.solids) piece.points.push(...meshPoints(solid));
  }
  if (plates.holes.length > 0) {
    piece.subtract = [...(piece.subtract ?? []), ...plates.holes];
  }
}

/** Round HSS base plates where a column sits on a square concrete pad. */
export function applyFooterEndplates(
  pieces: SupportPiece[],
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): void {
  if (!settings.hssEndplateEnabled) return;
  const footers = pieces.filter((p) => p.footerPos);
  const headed = new Set<string>();
  const plated = new Set<string>();
  const up = new Vector3(0, 1, 0);
  const down = new Vector3(0, -1, 0);
  for (const piece of pieces) {
    if (!piece.memberEnds || piece.memberEnds.length === 0) continue;
    for (const end of piece.memberEnds) {
      let best: SupportPiece | null = null;
      let bestDist = FOOTER_MATCH_M;
      for (const foot of footers) {
        const d = horizontalDist(foot.footerPos!, end.pos);
        const dy = Math.abs(foot.footerPos!.y - end.pos.y);
        // Glue face only — not the stub/column splice above the pad.
        if (d < bestDist && dy < 0.18) {
          bestDist = d;
          best = foot;
        }
      }
      if (!best?.footerPos) continue;
      const origin = best.footerPos.clone();
      const tubeR = piece.beamRadius ?? JOINT_PIN_RADIUS_M;
      const along = end.along.lengthSq() > 1e-12 ? end.along.clone().normalize() : down;
      const secant = footerPlateSecant(along.y);
      const padH = Math.max(pieceHeightY(best), 0.12);
      const footKey = hubKey(best.footerPos);
      const memberKey = `${footKey}@${end.pos.x.toFixed(3)},${end.pos.z.toFixed(3)}`;
      if (!plated.has(memberKey)) {
        plated.add(memberKey);
        const heads = !headed.has(footKey);
        if (heads) headed.add(footKey);
        addHssEndplate(piece, origin, up, tubeR, settings, {
          tubeSecant: secant,
          boltHeads: heads,
        });
        if (heads) {
          addHssEndplate(best, origin, down, tubeR, settings, {
            tubeSecant: secant,
            maxThickness: padH * 0.85,
          });
        }
      }
    }
  }
}

function pieceHeightY(piece: SupportPiece): number {
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of piece.points) {
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  if (!Number.isFinite(minY) || !Number.isFinite(maxY)) return 0;
  return maxY - minY;
}

function connectChainCuts(
  maleGroup: SupportPiece[],
  femaleGroup: SupportPiece[],
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): void {
  // 1:1 assignment so a socket cannot be claimed by two pins.
  const pairs: ChainPair[] = [];
  for (const male of maleGroup) {
    if (!male.chainPin || male.chainPin.applied) continue;
    for (const female of femaleGroup) {
      if (female === male) continue;
      const dist = chainCutDistance(male, female);
      if (dist == null) continue;
      pairs.push({
        male,
        female,
        dist,
        sameChain: male.chainId != null && female.chainId === male.chainId,
      });
    }
  }
  pairs.sort((a, b) => {
    if (a.sameChain !== b.sameChain) return a.sameChain ? -1 : 1;
    return a.dist - b.dist;
  });
  for (const pair of pairs) {
    if (pair.male.chainPin?.applied || pair.female.chainSocket?.applied) continue;
    applyChainJoint(pair.male, pair.female, settings);
  }
}

export function applyJointsAcrossGroups(
  groups: SupportPiece[][],
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): void {
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      applyCrossPackHubJoints(groups[i], groups[j]);
    }
  }
  // Mid-beam HSSS: always apply at every matching pin/socket, including when both
  // halves landed in the same pack (flanges stay in the unioned chunk solid).
  const all = groups.flat();
  const pairs: ChainPair[] = [];
  for (const male of all) {
    if (!male.chainPin || male.chainPin.applied) continue;
    for (const female of all) {
      if (female === male) continue;
      const dist = chainCutDistance(male, female);
      if (dist == null) continue;
      pairs.push({
        male,
        female,
        dist,
        sameChain: male.chainId != null && female.chainId === male.chainId,
      });
    }
  }
  pairs.sort((a, b) => {
    if (a.sameChain !== b.sameChain) return a.sameChain ? -1 : 1;
    return a.dist - b.dist;
  });
  for (const pair of pairs) {
    if (pair.male.chainPin?.applied || pair.female.chainSocket?.applied) continue;
    applyChainJoint(pair.male, pair.female, settings);
  }
}

/** Per track chunk (same numbering as track-NNN parts): applied support click-fits. */
function logTrackChunkSupportConnectors(
  onTrackByChunk: SupportPiece[][],
  logLine: (line: string) => void,
): void {
  for (let ti = 0; ti < onTrackByChunk.length; ti++) {
    const joints: string[] = [];
    for (const piece of onTrackByChunk[ti] ?? []) {
      if (piece.chainPin?.applied) {
        const o = piece.chainPin.origin;
        joints.push(
          `female@(${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) chain=${piece.chainId}#${piece.chainIndex}`,
        );
      }
      if (piece.chainSocket?.applied) {
        const o = piece.chainSocket.origin;
        joints.push(
          `female@(${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) chain=${piece.chainId}#${piece.chainIndex}`,
        );
      }
      for (const hp of piece.hubPins ?? []) {
        if (!hp.applied) continue;
        const o = hp.origin;
        joints.push(`hubPin@(${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) key=${hp.hubKey}`);
      }
      if (piece.hubPos && (piece.additives?.length ?? 0) > 0) {
        const o = piece.hubPos;
        joints.push(
          `hubSleeve@(${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) n=${piece.additives!.length}`,
        );
      }
    }
    if (joints.length > 0) {
      logLine(`track-${pad(ti + 1)} support connectors: ${joints.join('; ')}`);
    }
  }
}

/**
 * Summarise which support connectors were applied.
 * Unapplied pins whose mate exists are failures (HSSS is required even in-pack).
 */
export function auditSupportConnectors(groups: SupportPiece[][]): {
  lines: string[];
  warnings: string[];
} {
  const lines: string[] = [];
  const warnings: string[] = [];
  const pieceGroup = new Map<SupportPiece, number>();
  for (let gi = 0; gi < groups.length; gi++) {
    for (const p of groups[gi]) pieceGroup.set(p, gi);
  }

  let hubApplied = 0;
  let hubSamePack = 0;
  let hubOrphan = 0;
  let chainApplied = 0;
  let chainOrphan = 0;

  for (let gi = 0; gi < groups.length; gi++) {
    for (const member of groups[gi]) {
      for (const pin of member.hubPins ?? []) {
        if (pin.applied) {
          hubApplied += 1;
          // A pin/sleeve into a joint means the pack boundary landed on that
          // joint instead of halfway along a beam.
          const o = pin.origin;
          warnings.push(
            `Pack boundary falls on a joint at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) ` +
              `— beam and joint print as separate parts`,
          );
          continue;
        }
        const mateSame = groups[gi].some((h) => {
          const pos = h.hubPos ?? h.footerPos;
          return pos != null && hubKey(pos) === pin.hubKey;
        });
        const mateGi = groups.findIndex(
          (g, idx) =>
            idx !== gi &&
            g.some((h) => {
              const pos = h.hubPos ?? h.footerPos;
              return pos != null && hubKey(pos) === pin.hubKey;
            }),
        );
        if (mateGi >= 0) {
          hubOrphan += 1;
          const o = pin.origin;
          warnings.push(
            `Hub connector missing between packs ${gi}↔${mateGi} at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) key=${pin.hubKey}`,
          );
        } else if (mateSame) {
          hubSamePack += 1;
        } else {
          hubOrphan += 1;
          const o = pin.origin;
          warnings.push(
            `Hub connector mate missing for pin at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) key=${pin.hubKey}`,
          );
        }
      }

      if (member.chainPin) {
        if (member.chainPin.applied) {
          chainApplied += 1;
          continue;
        }
        const female = findChainSocketMate(member, pieceGroup.keys());
        chainOrphan += 1;
        const o = member.chainPin.origin;
        if (female) {
          warnings.push(
            `Mid-beam HSSS missing on chain ${member.chainId}#${member.chainIndex}→${female.chainIndex} ` +
              `packs ${gi}↔${pieceGroup.get(female)} at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)})`,
          );
        } else {
          warnings.push(
            `Mid-beam HSSS mate missing for chain ${member.chainId}#${member.chainIndex} ` +
              `at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)})`,
          );
        }
      }
    }
  }

  lines.push(
    `Support connectors: hub applied=${hubApplied} samePack=${hubSamePack} orphan=${hubOrphan}; ` +
      `mid-beam HSSS applied=${chainApplied} orphan=${chainOrphan}`,
  );
  return { lines, warnings };
}

/**
 * When support pieces in different print packs occupy the same space (crossing
 * braces, etc.), subtract each solid from the other so both print with a mating
 * cut instead of two overlapping solids.
 *
 * Skips pairs already joined by hub or mid-member connectors.
 */
export function applyCrossPackIntersectionCuts(groups: SupportPiece[][]): void {
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      for (const a of groups[i]) {
        for (const b of groups[j]) {
          if (skipCrossPackIntersectionCut(a, b)) continue;
          if (!pointsAabbOverlap(a.points, b.points)) continue;
          a.subtract = [...(a.subtract ?? []), b.mesh];
          b.subtract = [...(b.subtract ?? []), a.mesh];
        }
      }
    }
  }
}

/** True when A and B already share a hub pin/sleeve or an adjacent chain cut. */
function skipCrossPackIntersectionCut(a: SupportPiece, b: SupportPiece): boolean {
  if (a.mesh.indices.length < 3 || b.mesh.indices.length < 3) return true;
  if (hubConnected(a, b) || hubConnected(b, a)) return true;
  if (chainAdjacent(a, b) || chainCutConnected(a, b)) return true;
  // Crossing beams that meet at the same auto/elbow sphere use hub connectors.
  if (shareHubKey(a, b)) return true;
  // Pieces that already carry a mid-beam or hub connector must not be boolean-carved
  // by unrelated struts in the other pack — that eats the joint.
  if (hasAppliedClickFit(a) || hasAppliedClickFit(b)) return true;
  return false;
}

function hasAppliedClickFit(p: SupportPiece): boolean {
  if (p.chainPin?.applied || p.chainSocket?.applied) return true;
  if ((p.hubPins ?? []).some((h) => h.applied)) return true;
  return false;
}

/** Mid-beam halves that meet at the same cut origin — even if chainIndex desynced. */
function chainCutConnected(a: SupportPiece, b: SupportPiece): boolean {
  if (a.chainPin && b.chainSocket) {
    if (a.chainPin.origin.distanceTo(b.chainSocket.origin) <= CHAIN_CUT_MATCH_M) return true;
  }
  if (b.chainPin && a.chainSocket) {
    if (b.chainPin.origin.distanceTo(a.chainSocket.origin) <= CHAIN_CUT_MATCH_M) return true;
  }
  return false;
}

function shareHubKey(a: SupportPiece, b: SupportPiece): boolean {
  const keys = new Set((a.hubPins ?? []).map((p) => p.hubKey));
  if ((b.hubPins ?? []).some((p) => keys.has(p.hubKey))) return true;
  // Also treat member ends that meet within hub match as the same junction.
  for (const ea of a.memberEnds ?? []) {
    for (const eb of b.memberEnds ?? []) {
      if (ea.pos.distanceTo(eb.pos) <= HUB_MATCH_M) return true;
    }
  }
  return false;
}

function hubConnected(hubSide: SupportPiece, memberSide: SupportPiece): boolean {
  const pos = hubSide.hubPos ?? hubSide.footerPos;
  if (!pos) return false;
  const key = hubKey(pos);
  return (memberSide.hubPins ?? []).some((p) => p.hubKey === key);
}

function chainAdjacent(a: SupportPiece, b: SupportPiece): boolean {
  if (!a.chainId || a.chainId !== b.chainId) return false;
  if (a.chainIndex == null || b.chainIndex == null) return false;
  return Math.abs(a.chainIndex - b.chainIndex) === 1;
}

function pointsAabbOverlap(a: Vector3[], b: Vector3[], pad = 0.05): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const aa = pointsAabb(a);
  const bb = pointsAabb(b);
  return (
    aa.minX - pad <= bb.maxX + pad &&
    aa.maxX + pad >= bb.minX - pad &&
    aa.minY - pad <= bb.maxY + pad &&
    aa.maxY + pad >= bb.minY - pad &&
    aa.minZ - pad <= bb.maxZ + pad &&
    aa.maxZ + pad >= bb.minZ - pad
  );
}

function pointsAabb(points: Vector3[]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
} {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, maxX, minY, maxY, minZ, maxZ };
}

/**
 * Female socket on the track for a support pin.
 * Pin dir points from support into the track; the sleeve/cavity must use the
 * opposite axis so the hole lives inside the track and overlaps the pin.
 * Shared right/up keeps the D-key aligned after the dir flip.
 */
export function addTrackSupportSocket(
  meshes: SweepMesh[],
  subtract: SweepMesh[],
  pin: TrackPinSpec,
): void {
  const basis = jointBasis(pin.origin, pin.dir);
  const femaleDir = basis.dir.clone().multiplyScalar(-1);
  meshes.push(sleeveMesh(pin.origin, femaleDir, basis.right, basis.up));
  subtract.push(sleeveCavityMesh(pin.origin, femaleDir, basis.right, basis.up));
}

function withTrackPinApplied(piece: SupportPiece): SupportPiece {
  if (!piece.trackPin) return piece;
  const pin = pinMesh(piece.trackPin.origin, piece.trackPin.dir);
  return {
    ...piece,
    additives: [...(piece.additives ?? []), pin],
    points: [...piece.points, ...meshPoints(pin)],
    trackPin: undefined,
  };
}

/**
 * Leftover rail-attach beams that did not fuse onto a track chunk get the
 * original male pin; the owning track interval gets the matching female socket.
 */
function applyOverflowTrackSockets(
  trackChunks: TrackChunkDraft[],
  leftover: SupportPiece[],
  runtime: TrackRuntime,
): number {
  let n = 0;
  for (let i = 0; i < leftover.length; i++) {
    const piece = leftover[i]!;
    if (!piece.trackPin) continue;
    const pin = piece.trackPin;
    leftover[i] = withTrackPinApplied(piece);
    const ci = trackChunkIndexForAttach(nearestS(runtime, pin.origin), trackChunks);
    if (ci < 0) continue;
    addTrackSupportSocket(trackChunks[ci]!.meshes, trackChunks[ci]!.subtract, pin);
    n += 1;
  }
  return n;
}

function markTrackAttachIfNearRail(
  chain: Omit<SupportPiece, 's'>[],
  a: Vector3,
  b: Vector3,
  runtime: TrackRuntime,
): void {
  const s = nearestS(runtime, a);
  if (runtime.frameAt(s).railPos.distanceTo(a) > TRACK_ATTACH_MAX_M) return;
  markTrackAttach(chain, a, b);
}

function markTrackAttach(chain: Omit<SupportPiece, 's'>[], a: Vector3, b: Vector3): void {
  if (chain.length === 0) return;
  const dir = new Vector3().subVectors(a, b);
  if (dir.lengthSq() < 1e-12) return;
  dir.normalize();
  // Mutate in place — callers may pass a filtered view of the real chain array.
  chain[0].trackPin = { origin: a.clone(), dir };
}

/**
 * Slice a member into beam segments at existing hubs and HSSS max-beam cuts.
 * Hubs divide the member into spans; each span is cut into equal segments no
 * longer than maxBeamLengthMm. No cube-fit or hub-half mid-splits.
 */
export function splitCylinderChainWithJoints(
  a: Vector3,
  b: Vector3,
  radiusA: number,
  radiusB: number,
  chunkSizeMm: number,
  existingHubs: { pos: Vector3; radius: number }[] = [],
  footerPads: { pos: Vector3; radius: number; height: number }[] = [],
  maxBeamLengthMm = 0,
): Omit<SupportPiece, 's'>[] {
  const len = Math.max(a.distanceTo(b), 1e-6);
  const ab = new Vector3().subVectors(b, a);
  const dir = ab.clone().multiplyScalar(1 / len);

  // Structural hubs only — they divide the member into spans; no fit spheres.
  type Station = { t: number; pos: Vector3 };
  const hubTs: Station[] = [];
  const addHubT = (t: number, pos: Vector3) => {
    if (t < 0.02 || t > 0.98) return;
    if (hubTs.some((s) => Math.abs(s.t - t) < 0.015)) return;
    hubTs.push({ t, pos: pos.clone() });
  };

  for (const h of existingHubs) {
    const hit = closestPointOnSegment(h.pos, a, b);
    if (hit.distanceTo(h.pos) > HUB_MATCH_M) continue;
    const t = new Vector3().subVectors(hit, a).dot(dir) / len;
    addHubT(t, hit);
  }
  hubTs.sort((x, y) => x.t - y.t);

  const stations: Station[] = [
    { t: 0, pos: a.clone() },
    ...hubTs,
    { t: 1, pos: b.clone() },
  ];

  const chainId = nextChainId(a, b);
  const out: Omit<SupportPiece, 's'>[] = [];
  let chainIndex = 0;

  for (let i = 0; i < stations.length - 1; i++) {
    const t0 = stations[i].t;
    const t1 = stations[i + 1].t;
    const p0 = stations[i].pos;
    const p1 = stations[i + 1].pos;
    const r0 = radiusA + (radiusB - radiusA) * t0;
    const r1 = radiusA + (radiusB - radiusA) * t1;
    if (p0.distanceTo(p1) < 1e-4) continue;

    const segs = splitCylinderSegments(p0, p1, r0, r1, chunkSizeMm, 0, maxBeamLengthMm);

    for (let s = 0; s < segs.length; s++) {
      const seg = segs[s];
      const capA = endOnFooterPad(seg.a, footerPads);
      const capB = endOnFooterPad(seg.b, footerPads);
      const mesh = cylinderMesh(seg.a, seg.b, seg.radiusA, seg.radiusB, {
        horizontalCapA: capA,
        horizontalCapB: capB,
      });
      const beamR = (seg.radiusA + seg.radiusB) * 0.5;
      const pinR = beamFitPinRadius(beamR);
      const along = new Vector3().subVectors(seg.b, seg.a);
      const piece: Omit<SupportPiece, 's'> = {
        mesh,
        points: meshPoints(mesh),
        chainId,
        chainIndex,
        beamRadius: beamR,
        footerCapEnd: [capA, capB],
      };
      if (along.lengthSq() >= 1e-12) {
        along.normalize();
        piece.memberEnds = [
          { pos: seg.a.clone(), along: along.clone().multiplyScalar(-1) },
          { pos: seg.b.clone(), along: along.clone() },
        ];
      }

      // HSSS connectors between max-beam segments (never at hubs).
      if (s < segs.length - 1) {
        piece.chainPin = {
          origin: seg.b.clone(),
          dir: along.clone(),
          pinRadius: pinR,
        };
      }
      if (s > 0) {
        const prevR = (segs[s - 1].radiusA + segs[s - 1].radiusB) * 0.5;
        piece.chainSocket = {
          origin: seg.a.clone(),
          dir: along.clone(),
          pinRadius: beamFitPinRadius(prevR),
        };
      }

      out.push(piece);
      chainIndex += 1;
    }
  }

  return out;
}

let chainSeq = 0;
function nextChainId(a: Vector3, b: Vector3): string {
  chainSeq += 1;
  return `c${chainSeq}:${a.x.toFixed(2)},${a.y.toFixed(2)},${a.z.toFixed(2)}>${b.x.toFixed(2)},${b.y.toFixed(2)},${b.z.toFixed(2)}`;
}

interface CylSeg {
  a: Vector3;
  b: Vector3;
  radiusA: number;
  radiusB: number;
}

/**
 * Split a cylinder into equal segments no longer than maxBeamLengthMm (HSSS).
 * Does not cube-fit mid-split — oversized leftovers are marked later by packing.
 */
function splitCylinderSegments(
  a: Vector3,
  b: Vector3,
  radiusA: number,
  radiusB: number,
  _chunkSizeMm: number,
  depth = 0,
  maxBeamLengthMm = 0,
): CylSeg[] {
  const len = Math.max(a.distanceTo(b), 1e-6);
  const maxLenM = maxBeamLengthM({ ...DEFAULT_PRINT_SETTINGS, maxBeamLengthMm });
  if (!(Number.isFinite(maxLenM) && len > maxLenM + 1e-6)) {
    return [{ a: a.clone(), b: b.clone(), radiusA, radiusB }];
  }

  if (depth > 24 || len < 1e-3) {
    return [{ a: a.clone(), b: b.clone(), radiusA, radiusB }];
  }

  const n = equalBeamSegmentCount(len, maxLenM);
  const out: CylSeg[] = [];
  for (let i = 0; i < n; i++) {
    const t0 = i / n;
    const t1 = (i + 1) / n;
    const p0 = a.clone().lerp(b, t0);
    const p1 = a.clone().lerp(b, t1);
    const r0 = radiusA + (radiusB - radiusA) * t0;
    const r1 = radiusA + (radiusB - radiusA) * t1;
    out.push({ a: p0, b: p1, radiusA: r0, radiusB: r1 });
  }
  return out;
}

/** Geometry-only HSSS length slices (no joints) — used by tests. */
export function splitCylinderToFit(
  a: Vector3,
  b: Vector3,
  radiusA: number,
  radiusB: number,
  maxBeamLengthMm: number,
): SweepMesh[] {
  return splitCylinderSegments(a, b, radiusA, radiusB, 0, 0, maxBeamLengthMm).map((s) =>
    cylinderMesh(s.a, s.b, s.radiusA, s.radiusB),
  );
}

function nearestS(runtime: TrackRuntime, p: Vector3): number {
  const L = runtime.totalLength;
  const step = Math.max(0.5, L / 400);
  let bestS = 0;
  let bestD = Infinity;
  for (let s = 0; s <= L; s += step) {
    const d = runtime.frameAt(s).railPos.distanceToSquared(p);
    if (d < bestD) {
      bestD = d;
      bestS = s;
    }
  }
  const lo = Math.max(0, bestS - step);
  const hi = Math.min(L, bestS + step);
  for (let s = lo; s <= hi; s += step / 10) {
    const d = runtime.frameAt(s).railPos.distanceToSquared(p);
    if (d < bestD) {
      bestD = d;
      bestS = s;
    }
  }
  return bestS;
}

/** Arc-length of every stub↔rail connection (beam 1 track end). */
function supportAttachArcLengths(
  runtime: TrackRuntime,
  prims: TrackSupportPrimitive[],
): number[] {
  const out: number[] = [];
  for (const prim of prims) {
    if (prim.kind === 'stub') {
      if (prim.attachS != null && Number.isFinite(prim.attachS)) {
        out.push(prim.attachS);
        continue;
      }
      // Footer attach stubs have no attachS and live on the pad — skip them.
      const sA = nearestS(runtime, prim.a);
      const sB = nearestS(runtime, prim.b);
      const da = runtime.frameAt(sA).railPos.distanceTo(prim.a);
      const db = runtime.frameAt(sB).railPos.distanceTo(prim.b);
      if (da <= TRACK_ATTACH_MAX_M && da <= db + 1e-6) out.push(sA);
    } else if (prim.kind === 'column') {
      // Straight columns without a stub still pin near the rail at end `a`.
      const s = nearestS(runtime, prim.a);
      if (runtime.frameAt(s).railPos.distanceTo(prim.a) <= TRACK_ATTACH_MAX_M) {
        out.push(s);
      }
    }
  }
  return out;
}

function chunkIndexForS(s: number, chunks: TrackChunkDraft[]): number {
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i]!;
    const isLast = i === chunks.length - 1 && chunk.s0 <= chunk.s1;
    if (sInChunk(s, chunk.s0, chunk.s1, isLast)) return i;
  }
  return -1;
}

function sInChunk(s: number, s0: number, s1: number, isLast: boolean): boolean {
  if (s0 > s1 + 1e-12) return s >= s0 || s < s1;
  return s >= s0 && (s < s1 || (isLast && s <= s1 + 1e-6));
}

/**
 * Each joint-part is its own chunk. HSS splices already separated long beams,
 * so connected hubs stay together and cube size does not create extra packs.
 */
export function groupSupportPieces(
  items: SupportPiece[],
  _chunkSizeMm?: number,
): SupportPiece[][] {
  void _chunkSizeMm;
  if (items.length === 0) return [];
  return buildJointParts(items).map((p) => p.pieces);
}

/** Stamp remainders that share a mid-beam cut with a track-owned stump. */
export function markTrackCutSeeds(onTrack: SupportPiece[], remainder: SupportPiece[]): void {
  for (const r of remainder) {
    r.fromTrackCut = false;
    for (const t of onTrack) {
      if (
        r.chainId &&
        r.chainId === t.chainId &&
        r.chainIndex != null &&
        t.chainIndex != null &&
        Math.abs(r.chainIndex - t.chainIndex) === 1
      ) {
        r.fromTrackCut = true;
        break;
      }
    }
  }
}

function isJunctionPiece(p: SupportPiece): boolean {
  return !!(p.hubPos || p.footerPos);
}

function partBeams(part: SupportPart): SupportPiece[] {
  return part.pieces.filter(
    (p) => !isJunctionPiece(p) && !!p.memberEnds && p.memberEnds.length >= 2,
  );
}

function junctionsCoincide(a: SupportPiece, b: SupportPiece): boolean {
  if (a === b) return true;
  const ap = a.hubPos ?? a.footerPos;
  const bp = b.hubPos ?? b.footerPos;
  if (!ap || !bp) return false;
  if (a.footerPos || b.footerPos) {
    return (
      horizontalDist(ap, bp) <= FOOTER_MATCH_M && Math.abs(ap.y - bp.y) <= FOOTER_MATCH_M
    );
  }
  return ap.distanceTo(bp) <= HUB_MATCH_M;
}

function junctionEndIndex(beam: SupportPiece, junction: SupportPiece): number {
  const pos = junction.hubPos ?? junction.footerPos;
  if (!pos || !beam.memberEnds) return -1;
  return beam.memberEnds.findIndex((e) => {
    if (junction.footerPos) {
      return (
        horizontalDist(pos, e.pos) <= FOOTER_MATCH_M &&
        Math.abs(pos.y - e.pos.y) <= FOOTER_MATCH_M
      );
    }
    return e.pos.distanceTo(pos) <= HUB_MATCH_M;
  });
}

function beamTouchesJunction(beam: SupportPiece, junction: SupportPiece): boolean {
  if (junctionEndIndex(beam, junction) >= 0) return true;
  const pos = junction.hubPos ?? junction.footerPos;
  if (!pos) return false;
  for (const pin of beam.hubPins ?? []) {
    if (junction.hubPos && pin.hubKey === hubKey(junction.hubPos)) return true;
    if (pin.origin.distanceTo(pos) <= HUB_MATCH_M) return true;
    if (
      junction.footerPos &&
      horizontalDist(pin.origin, junction.footerPos) <= FOOTER_MATCH_M &&
      Math.abs(pin.origin.y - junction.footerPos.y) <= FOOTER_MATCH_M
    ) {
      return true;
    }
  }
  return false;
}

function trackChunkIndexForAttach(s: number, chunks: TrackChunkDraft[]): number {
  const direct = chunkIndexForS(s, chunks);
  if (direct >= 0) return direct;
  if (chunks.length === 0) return -1;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < chunks.length; i++) {
    const c = chunks[i]!;
    const mid = c.s0 <= c.s1 ? 0.5 * (c.s0 + c.s1) : c.s0;
    const d = Math.abs(s - mid);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/**
 * Fuse each rail-attach joint-part onto its track interval. Leftover
 * joint-parts become support chunks — HSS splices are the only remaining
 * boundaries (no cube-fill grow).
 */
function packPreviewSplitSupportOntoTrack(
  trackChunks: TrackChunkDraft[],
  allSupport: SupportPiece[],
  runtime: TrackRuntime,
): { onTrackByChunk: SupportPiece[][]; remainder: SupportPiece[] } {
  const onTrackByChunk: SupportPiece[][] = trackChunks.map(() => []);
  if (allSupport.length === 0 || trackChunks.length === 0) {
    return { onTrackByChunk, remainder: allSupport };
  }

  const parts = buildJointParts(allSupport);
  const assigned = new Set<SupportPart>();

  const attachSOf = (part: SupportPart): number => {
    const pin = part.pieces.find((p) => p.trackPin)?.trackPin;
    if (pin) return nearestS(runtime, pin.origin);
    return partSortKey(part);
  };

  for (const part of parts) {
    if (!isTrackSeedPart(part)) continue;
    const s = attachSOf(part);
    const ci = trackChunkIndexForAttach(s, trackChunks);
    if (ci < 0) continue;
    assigned.add(part);
    for (const piece of part.pieces) piece.s = s;
    onTrackByChunk[ci]!.push(...part.pieces);
  }

  const remainder = parts.filter((p) => !assigned.has(p)).flatMap((p) => p.pieces);
  return { onTrackByChunk, remainder };
}


function isTrackSeedPart(part: SupportPart): boolean {
  return part.pieces.some((p) => !!(p.trackPin || p.fromTrackCut));
}

function partSortKey(part: SupportPart): number {
  return part.pieces.reduce((s, p) => Math.min(s, p.s), Infinity);
}

/**
 * Bundle connected support pieces into print atoms. Hubs/footers stay with
 * every beam that meets them. Beams that meet end-to-end stay together unless
 * that meeting is an HSSS max-beam splice (the only beam split).
 */
export function buildJointParts(items: SupportPiece[]): SupportPart[] {
  const pool: SupportPiece[] = [...items];
  const parentOf = new Map<SupportPiece, SupportPiece>();
  const find = (p: SupportPiece): SupportPiece => {
    let root = p;
    while ((parentOf.get(root) ?? root) !== root) root = parentOf.get(root)!;
    parentOf.set(p, root);
    return root;
  };
  const union = (a: SupportPiece, b: SupportPiece): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parentOf.set(rb, ra);
  };
  for (const p of pool) parentOf.set(p, p);

  const junctions = pool.filter(isJunctionPiece);
  for (let i = 0; i < junctions.length; i++) {
    for (let k = i + 1; k < junctions.length; k++) {
      if (junctionsCoincide(junctions[i], junctions[k])) union(junctions[i], junctions[k]);
    }
  }

  const beams = pool.filter((p) => !isJunctionPiece(p));
  for (const beam of beams) {
    for (const j of junctions) {
      if (beamTouchesJunction(beam, j)) union(beam, j);
    }
  }
  for (let i = 0; i < beams.length; i++) {
    for (let k = i + 1; k < beams.length; k++) {
      if (isHssSpliceMeet(beams[i], beams[k])) continue;
      if (beamsShareEnd(beams[i], beams[k])) union(beams[i], beams[k]);
    }
  }

  const byRoot = new Map<SupportPiece, SupportPiece[]>();
  for (const p of pool) {
    const root = find(p);
    const list = byRoot.get(root);
    if (list) list.push(p);
    else byRoot.set(root, [p]);
  }

  const parts: SupportPart[] = [];
  for (const members of byRoot.values()) {
    const footer = members.find((m) => m.footerPos) ?? null;
    const pieces: SupportPiece[] = [];
    for (const p of members) {
      if (footer && p !== footer && p.hubPos && junctionsCoincide(footer, p)) continue;
      pieces.push(p);
    }
    if (footer) {
      const droppedKeys = new Set(
        members
          .filter((j) => j.hubPos && junctionsCoincide(footer, j))
          .map((j) => hubKey(j.hubPos!)),
      );
      for (const piece of pieces) {
        if (!piece.hubPins || droppedKeys.size === 0) continue;
        piece.hubPins = piece.hubPins.filter((pin) => !droppedKeys.has(pin.hubKey));
        if (piece.hubPins.length === 0) piece.hubPins = undefined;
      }
    }
    const junction = footer ?? members.find(isJunctionPiece) ?? null;
    parts.push({ pieces, junction });
  }

  attachHalfBeamsToBareFooters(parts);
  return shrinkJointPartsToFit(parts.filter((p) => p.pieces.length > 0));
}

function beamsShareEnd(a: SupportPiece, b: SupportPiece): boolean {
  for (const ea of a.memberEnds ?? []) {
    for (const eb of b.memberEnds ?? []) {
      if (ea.pos.distanceTo(eb.pos) <= CHAIN_CUT_MATCH_M) return true;
    }
  }
  return false;
}

/** True when two beam pieces meet at an HSSS max-beam splice, not a hub. */
function isHssSpliceMeet(a: SupportPiece, b: SupportPiece): boolean {
  return chainCutConnected(a, b) || chainAdjacent(a, b);
}

/** Steal a beam for any footer that ended up as a bare pad (merge — never mid-split). */
function attachHalfBeamsToBareFooters(parts: SupportPart[]): void {
  for (const part of parts) {
    const footer = part.junction;
    if (!footer?.footerPos) continue;
    if (partBeams(part).length > 0) continue;

    for (const other of parts) {
      if (other === part) continue;
      const beam = partBeams(other).find((b) => beamTouchesJunction(b, footer));
      if (!beam) continue;
      // Merge rather than mid-split — only HSSS max-beam cuts create boundaries.
      part.pieces.push(...other.pieces);
      other.pieces = [];
      break;
    }
  }
}

/**
 * Packing no longer mid-splits beams to fit the cube. HSSS max-beam cuts are the
 * only beam boundaries; oversized parts are flagged later.
 */
function shrinkJointPartsToFit(partsIn: SupportPart[]): SupportPart[] {
  const parts = partsIn.filter((p) => p.pieces.length > 0);
  const all = parts.flatMap((p) => p.pieces);
  linkMemberEndsToHubs(all);
  linkMemberEndsToFooters(all);
  return parts;
}


function pad(n: number): string {
  return String(n).padStart(3, '0');
}

function nonemptyMesh(mesh: SweepMesh | undefined): mesh is SweepMesh {
  return !!mesh && mesh.indices.length > 0;
}

function atomMeshFromPiece(piece: SupportPiece): SweepMesh | null {
  const meshes = [piece.mesh, ...(piece.additives ?? [])].filter(nonemptyMesh);
  if (meshes.length === 0) return null;
  return meshes.length === 1 ? meshes[0] : mergeMeshes(meshes);
}

/** Snapshot every split fragment before they were unioned into print chunks. */
function collectPrintAtoms(
  trackChunks: TrackChunkDraft[],
  onTrackByChunk: SupportPiece[][],
  supportGroups: SupportPiece[][],
  parts: PrintPart[],
): PrintAtom[] {
  const atoms: PrintAtom[] = [];
  let n = 0;
  const push = (chunkId: string, kind: PrintPart['kind'], mesh: SweepMesh | null): void => {
    if (!mesh || mesh.indices.length === 0) return;
    n += 1;
    atoms.push({ id: `atom-${pad(n)}`, chunkId, kind, mesh });
  };

  for (let ci = 0; ci < trackChunks.length; ci++) {
    const chunkId = `track-${pad(ci + 1)}`;
    const meshes = trackChunks[ci].meshes.filter(nonemptyMesh);
    push(
      chunkId,
      'track',
      meshes.length === 0 ? null : meshes.length === 1 ? meshes[0] : mergeMeshes(meshes),
    );
    for (const piece of onTrackByChunk[ci] ?? []) {
      push(chunkId, 'support', atomMeshFromPiece(piece));
    }
  }
  for (let si = 0; si < supportGroups.length; si++) {
    const chunkId = `support-${pad(si + 1)}`;
    for (const piece of supportGroups[si]) {
      push(chunkId, 'support', atomMeshFromPiece(piece));
    }
  }
  for (const part of parts) {
    switch (part.kind) {
      case 'connector':
        push(part.id, 'connector', part.mesh);
        break;
      case 'ground':
        push(part.id, 'ground', part.mesh);
        break;
      case 'track':
      case 'support':
        break;
      default: {
        const _never: never = part.kind;
        void _never;
      }
    }
  }
  return atoms;
}

function fmtObb(obb: ObbMm): string {
  return `${obb.x.toFixed(0)}×${obb.y.toFixed(0)}×${obb.z.toFixed(0)} mm`;
}
